import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { cigarApi } from './cigarApi'
import { helpApi } from './helpApi'

export interface AdminUser {
  id: string
  email: string
  username: string
  firstName?: string
  lastName?: string
  role: string
  roleExpiresAt?: string | null
  subscriptionTier?: string
  permissions?: string[]
  lastLoginAt?: string
  lastAdminLogin?: string
  adminNotes?: string
  isActive: boolean
  createdBy?: string
  createdAt: string
  updatedAt: string
}

export interface SystemMetrics {
  totalUsers: number
  activeUsers: number
  newUsersToday: number
  subscriptionBreakdown: Record<string, number>
  averageResponseTime: number
  errorRate: number
  uptime: number
  totalCigars: number
  totalSessions: number
  averageRating: number | null
  collectionValue: number
  databaseConnections: number
  memoryUsage: number
  cpuUsage: number
  diskUsage: number
}

export interface AdminDashboardData {
  metrics: SystemMetrics
  recentUsers: AdminUser[]
  recentTickets: any[]
  systemHealth: {
    status: 'healthy' | 'warning' | 'critical'
    issues: string[]
  }
  auditLogs: any[]
}

export type FeatureUseSummaryRange = '7d' | '30d'

export interface FeatureUseSummaryEventRow {
  eventName: string
  count: number
  bucketId: string
}

export interface FeatureUseSummary {
  enabled: boolean
  disabledReason?: string
  range: FeatureUseSummaryRange
  rangeDays: number
  total: number
  byEventName: FeatureUseSummaryEventRow[]
  byViewport?: { xs: number; mdPlus: number }
}

export interface CatalogBrandCoverage {
  brandId: string
  brandName: string
  country: string | null
  lineCount: number
  sizedLines: number
  emptyLines: number
  missingWrapper: number
  missingBinder: number
  missingFiller: number
  missingStrength: number
  pctSized: number
  depthBucket: '1' | '2-3' | '4-8' | '9+'
}

export interface CatalogStats {
  summary: {
    brands: number
    lines: number
    vitolas: number
    lineVitolaLinks: number
    communityCigars: number
    sizedLines: number
    emptyLines: number
    pctSized: number
    pctEmpty: number
  }
  leaf: {
    wrapperPct: number
    binderPct: number
    fillerPct: number
    strengthPct: number
    completeLeafPct: number
    brandCountryPct: number
  }
  depthBuckets: Record<'1' | '2-3' | '4-8' | '9+', number>
  hotspots: CatalogBrandCoverage[]
  brands: CatalogBrandCoverage[]
}

export interface CreateAdminUserRequest {
  email: string
  username: string
  password: string
  firstName?: string
  lastName?: string
  role: 'super_admin' | 'admin' | 'moderator'
  adminNotes?: string
}

export interface UpdateAdminUserRequest {
  email?: string
  username?: string
  firstName?: string
  lastName?: string
  role?: 'super_admin' | 'admin' | 'moderator'
  adminNotes?: string
  isActive?: boolean
}

export interface UserManagementFilters {
  role?: string
  subscriptionTier?: string
  isActive?: boolean
  search?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// Moderation types
export type ModerationStatus = 'pending' | 'reviewed' | 'resolved' | 'dismissed'
export type ModerationSeverity = 'low' | 'medium' | 'high' | 'critical'
export type ModerationContentType = 'post' | 'comment' | 'review' | 'user'
export type ModerationActionType = 'approve' | 'reject' | 'delete' | 'warn' | 'ban' | 'dismiss'

export interface ModerationItem {
  id: string
  contentType: ModerationContentType
  contentId: string
  contentExcerpt?: string
  reporterId?: string
  reportedUserId?: string
  reason: string
  severity: ModerationSeverity
  status: ModerationStatus
  notes?: string
  createdAt: string
  updatedAt: string
  resolvedAt?: string
  reporter?: { id: string; username: string }
  reportedUser?: { id: string; username: string }
  actions: ModerationActionRecord[]
}

export interface ModerationActionRecord {
  id: string
  itemId: string
  adminId: string
  action: ModerationActionType
  reason?: string
  createdAt: string
  admin: { id: string; username: string }
}

export interface ModerationQueueFilters {
  status?: ModerationStatus
  contentType?: ModerationContentType
  severity?: ModerationSeverity
  dateFrom?: string
  dateTo?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface ModerationStats {
  byStatus: Record<string, number>
  bySeverity: Record<string, number>
  byType: Record<string, number>
}

export interface AdminApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
    details?: any
  }
  meta?: {
    timestamp: string
    version: string
    pagination?: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }
}

export interface FlaggedItem {
  id: string
  contentType: string
  contentId: string
  reason: string
  details?: string
  status: 'pending' | 'approved' | 'rejected' | 'deleted'
  createdAt: string
  reporter?: {
    id: string
    email: string
    username: string
  }
}

export interface DetailedMetrics extends SystemMetrics {
  errorLogs: Array<{
    id: string
    level: string
    message: string
    path?: string
    method?: string
    createdAt: string
  }>
  dbConnectionStatus: 'connected' | 'disconnected' | 'error'
  recentRegistrations24h: number
  currentActiveSessions: number
  suspendedAccounts: number
  communityStats: {
    totalReviews: number
    totalSessions: number
    activeDiscussions: number
    flaggedContentCount: number
  }
}

// ─── Audit Log Types ──────────────────────────────────────────────────────────

export type AuditActionType =
  | 'CREATE' | 'READ' | 'UPDATE' | 'DELETE'
  | 'LOGIN' | 'LOGOUT' | 'LOGIN_FAILED' | 'AUTH_FAILURE'
  | 'CONFIG_CHANGE' | 'BULK_DELETE' | 'PRIVILEGE_ESCALATION' | 'OTHER'

export type AuditEventStatus = 'success' | 'failure' | 'error'
export type AuditEventSeverity = 'info' | 'warning' | 'error' | 'critical'

export interface AuditLogEntry {
  id: string
  adminId?: string | null
  action: string
  actionType: AuditActionType
  resource: string
  resourceType: string
  resourceId?: string | null
  actorType: 'user' | 'admin' | 'system'
  status: AuditEventStatus
  severity: AuditEventSeverity
  sessionId?: string | null
  ipAddress?: string | null
  userAgent?: string | null
  integrityHash?: string | null
  retentionCategory: 'standard' | 'sensitive' | 'security'
  timestamp: string
  archivedAt?: string | null
  beforeState?: { role?: string } | null
  afterState?: { role?: string } | null
  admin?: { id: string; email: string; username: string } | null
}

export interface AuditLogFilters {
  actorId?: string
  actionType?: AuditActionType
  resourceType?: string
  resourceId?: string
  status?: AuditEventStatus
  severity?: AuditEventSeverity
  startDate?: string
  endDate?: string
  search?: string
  page?: number
  limit?: number
}

export interface AuditStats {
  totalEvents: number
  byActionType: Record<string, number>
  byResourceType: Record<string, number>
  bySeverity: Record<string, number>
  byStatus: Record<string, number>
  failedAuthEvents: number
  suspiciousEvents: number
}

// ─── Role Management Types ────────────────────────────────────────────────────

export type AdminRole = 'super_admin' | 'admin' | 'moderator' | 'user'

export interface RoleDefinition {
  role: AdminRole
  level: number
  permissions: string[]
  description: string
}

export interface PermissionMatrixRow {
  permission: string
  roles: AdminRole[]
}

export interface RoleDefinitionsPayload {
  roles: RoleDefinition[]
  matrix: PermissionMatrixRow[]
}

export interface MyAdminPermissions {
  role: string
  permissions: string[]
}

export interface SystemConfigEntry {
  id: string
  key: string
  value: unknown
  description?: string | null
  category: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UpsertSystemConfigRequest {
  key: string
  value: unknown
  description?: string
  category?: string
  isActive?: boolean
}

export interface AdminIntegration {
  id: string
  name: string
  type: string
  status: string
  configuration: Record<string, unknown>
  lastSync?: string | null
  errorCount: number
  createdAt: string
  updatedAt: string
}

export interface RoleAssignment {
  userId: string
  role: AdminRole
  roleExpiresAt?: string | null
  adminNotes?: string
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['AdminUser', 'AdminDashboard', 'SystemMetrics', 'ModerationItem', 'ModerationStats', 'FlaggedContent', 'AuditLog', 'RoleDefinition', 'Brand', 'CigarLine', 'AdminSubscription', 'AdminCmsContent', 'SystemConfig', 'AdminIntegration', 'CatalogStats'],
  endpoints: (builder) => ({
    // Dashboard
    getAdminDashboard: builder.query<AdminDashboardData, void>({
      query: () => '/dashboard',
      transformResponse: (response: AdminApiResponse<AdminDashboardData>) => response.data as AdminDashboardData,
      providesTags: ['AdminDashboard'],
    }),

    getCatalogStats: builder.query<CatalogStats, void>({
      query: () => '/catalog/stats',
      transformResponse: (response: AdminApiResponse<CatalogStats>) => response.data as CatalogStats,
      providesTags: ['CatalogStats'],
    }),

    // Detailed metrics (system health + error logs + community stats)
    getDetailedMetrics: builder.query<AdminApiResponse<DetailedMetrics>, void>({
      query: () => '/metrics',
      providesTags: ['SystemMetrics'],
    }),

    // Flagged content
    getFlaggedContent: builder.query<AdminApiResponse<FlaggedItem[]>, { status?: string }>({
      query: ({ status = 'pending' } = {}) => ({ url: '/content/flagged', params: { status } }),
      providesTags: ['FlaggedContent'],
    }),

    moderateFlaggedContent: builder.mutation<AdminApiResponse<FlaggedItem>, { id: string; action: 'approve' | 'reject' | 'delete'; reviewNotes?: string }>({
      query: ({ id, ...body }) => ({ url: `/content/flagged/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['FlaggedContent', 'SystemMetrics'],
    }),

    // User Management
    getUsers: builder.query<AdminApiResponse<AdminUser[]>, UserManagementFilters>({
      query: (filters) => ({
        url: '/users',
        params: filters,
      }),
      providesTags: ['AdminUser'],
    }),

    getUserById: builder.query<AdminApiResponse<AdminUser>, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'AdminUser', id }],
    }),

    createAdminUser: builder.mutation<AdminApiResponse<AdminUser>, CreateAdminUserRequest>({
      query: (userData) => ({
        url: '/users',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['AdminUser'],
    }),

    updateUser: builder.mutation<AdminApiResponse<AdminUser>, { id: string; updates: Record<string, unknown> }>({
      query: ({ id, updates }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['AdminUser'],
    }),

    suspendUser: builder.mutation<AdminApiResponse<AdminUser>, string>({
      query: (id) => ({
        url: `/users/${id}/suspend`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminUser'],
    }),

    activateUser: builder.mutation<AdminApiResponse<AdminUser>, string>({
      query: (id) => ({
        url: `/users/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminUser'],
    }),

    // System Health
    getSystemHealth: builder.query<AdminApiResponse<any>, void>({
      query: () => '/health',
      providesTags: ['SystemMetrics'],
    }),

    // Analytics
    getAnalyticsOverview: builder.query<AdminApiResponse<any>, void>({
      query: () => '/analytics/overview',
      providesTags: ['SystemMetrics'],
    }),

    getUserAnalytics: builder.query<AdminApiResponse<any>, void>({
      query: () => '/analytics/users',
      providesTags: ['SystemMetrics'],
    }),

    getPerformanceAnalytics: builder.query<AdminApiResponse<any>, void>({
      query: () => '/analytics/performance',
      providesTags: ['SystemMetrics'],
    }),

    getAdminFeatureUseSummary: builder.query<
      FeatureUseSummary,
      { range?: FeatureUseSummaryRange }
    >({
      query: ({ range = '30d' } = {}) => ({
        url: '/analytics/feature-use',
        params: { range },
      }),
      transformResponse: (response: AdminApiResponse<FeatureUseSummary>) =>
        response.data as FeatureUseSummary,
      providesTags: ['SystemMetrics'],
    }),

    // CMS content management
    getAdminCmsContent: builder.query<
      {
        items: Array<{
          id: string
          slug: string
          title: string
          body: string
          excerpt: string | null
          category: string
          contentType: string
          status: string
          tags: string[]
          sortOrder: number
          publishedAt: string | null
          updatedAt: string
        }>
        pagination: NonNullable<AdminApiResponse['meta']>['pagination']
      },
      { status?: string; category?: string; contentType?: string; search?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: '/content',
        params,
      }),
      transformResponse: (response: AdminApiResponse<Array<Record<string, unknown>>>) => ({
        items: (response.data ?? []) as Array<{
          id: string
          slug: string
          title: string
          body: string
          excerpt: string | null
          category: string
          contentType: string
          status: string
          tags: string[]
          sortOrder: number
          publishedAt: string | null
          updatedAt: string
        }>,
        pagination: response.meta?.pagination ?? { page: 1, limit: 50, total: 0, totalPages: 0 },
      }),
      providesTags: ['AdminCmsContent'],
    }),

    createAdminCmsContent: builder.mutation<
      AdminApiResponse<Record<string, unknown>>,
      {
        slug?: string
        title: string
        body: string
        excerpt?: string
        category?: string
        contentType?: string
        status?: string
        tags?: string[]
        sortOrder?: number
      }
    >({
      query: (body) => ({
        url: '/content',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminCmsContent'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(helpApi.util.invalidateTags(['HelpCenter']))
        } catch {
          // no-op
        }
      },
    }),

    updateAdminCmsContent: builder.mutation<
      AdminApiResponse<Record<string, unknown>>,
      {
        id: string
        updates: {
          slug?: string
          title?: string
          body?: string
          excerpt?: string | null
          category?: string
          contentType?: string
          status?: string
          tags?: string[]
          sortOrder?: number
        }
      }
    >({
      query: ({ id, updates }) => ({
        url: `/content/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'AdminCmsContent', id }, 'AdminCmsContent'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(helpApi.util.invalidateTags(['HelpCenter']))
        } catch {
          // no-op
        }
      },
    }),

    deleteAdminCmsContent: builder.mutation<AdminApiResponse<void>, string>({
      query: (id) => ({
        url: `/content/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminCmsContent'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(helpApi.util.invalidateTags(['HelpCenter']))
        } catch {
          // no-op
        }
      },
    }),

    // Brand Management
    createBrand: builder.mutation<AdminApiResponse<any>, any>({
      query: (brandData) => ({
        url: '/brands',
        method: 'POST',
        body: brandData,
      }),
      invalidatesTags: ['Brand'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['Brand']))
        } catch {
          // Mutation failed, no cross-slice invalidation needed.
        }
      },
    }),

    updateBrand: builder.mutation<AdminApiResponse<any>, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/brands/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Brand', id }, 'Brand'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['Brand']))
        } catch {
          // Mutation failed, no cross-slice invalidation needed.
        }
      },
    }),

    deleteBrand: builder.mutation<AdminApiResponse<void>, string>({
      query: (id) => ({
        url: `/brands/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Brand'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['Brand']))
        } catch {
          // Mutation failed, no cross-slice invalidation needed.
        }
      },
    }),

    // Cigar Line Management
    createCigarLine: builder.mutation<AdminApiResponse<any>, any>({
      query: (cigarLineData) => ({
        url: '/cigar-lines',
        method: 'POST',
        body: cigarLineData,
      }),
      invalidatesTags: ['CigarLine'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['CigarLine']))
        } catch {
          // Mutation failed, no cross-slice invalidation needed.
        }
      },
    }),

    updateCigarLine: builder.mutation<AdminApiResponse<any>, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/cigar-lines/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'CigarLine', id }, 'CigarLine'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['CigarLine']))
        } catch {
          // Mutation failed, no cross-slice invalidation needed.
        }
      },
    }),

    deleteCigarLine: builder.mutation<AdminApiResponse<void>, string>({
      query: (id) => ({
        url: `/cigar-lines/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CigarLine'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['CigarLine']))
        } catch {
          // Mutation failed, no cross-slice invalidation needed.
        }
      },
    }),

    // Subscription management (user subscriptionTier)
    getAdminSubscriptions: builder.query<
      {
        users: Array<{ id: string; email: string; username: string; subscriptionTier: string }>
        pagination: NonNullable<AdminApiResponse['meta']>['pagination']
      },
      { tier?: string; search?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: '/subscriptions',
        params,
      }),
      transformResponse: (response: AdminApiResponse<Array<{ id: string; email: string; username: string; subscriptionTier: string }>>) => ({
        users: response.data ?? [],
        pagination: response.meta?.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 },
      }),
      providesTags: ['AdminSubscription'],
    }),

    updateAdminSubscription: builder.mutation<
      AdminApiResponse<{ id: string; subscriptionTier: string }>,
      { id: string; subscriptionTier: string }
    >({
      query: ({ id, subscriptionTier }) => ({
        url: `/subscriptions/${id}`,
        method: 'PUT',
        body: { subscriptionTier },
      }),
      invalidatesTags: ['AdminSubscription', 'AdminUser', 'SystemMetrics'],
    }),

    cancelAdminSubscription: builder.mutation<AdminApiResponse<{ id: string; subscriptionTier: string }>, string>({
      query: (id) => ({
        url: `/subscriptions/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminSubscription', 'AdminUser', 'SystemMetrics'],
    }),

    restoreAdminUserBackup: builder.mutation<
      AdminApiResponse<{
        result: { restoredHumidors: number; imported: number; merged: number }
      }>,
      { userId: string; backup: unknown }
    >({
      query: ({ userId, backup }) => ({
        url: `/users/${userId}/backup/restore`,
        method: 'POST',
        body: { backup },
      }),
    }),

    // Support Tickets
    getSupportTickets: builder.query<AdminApiResponse<any[]>, any>({
      query: (filters) => ({
        url: '/support/tickets',
        params: filters,
      }),
      providesTags: ['AdminUser'],
    }),

    updateSupportTicket: builder.mutation<AdminApiResponse<any>, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/support/tickets/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'AdminUser', id }],
    }),

    respondToTicket: builder.mutation<AdminApiResponse<any>, { id: string; response: any }>({
      query: ({ id, response }) => ({
        url: `/support/tickets/${id}/respond`,
        method: 'POST',
        body: response,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'AdminUser', id }],
    }),

    // Moderation Queue
    getModerationStats: builder.query<AdminApiResponse<ModerationStats>, void>({
      query: () => '/moderation/stats',
      providesTags: ['ModerationStats'],
    }),

    getModerationQueue: builder.query<AdminApiResponse<ModerationItem[]>, ModerationQueueFilters>({
      query: (filters) => ({ url: '/moderation/queue', params: filters }),
      providesTags: ['ModerationItem'],
    }),

    getModerationItem: builder.query<AdminApiResponse<ModerationItem>, string>({
      query: (id) => `/moderation/queue/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'ModerationItem', id }],
    }),

    createModerationItem: builder.mutation<AdminApiResponse<ModerationItem>, Partial<ModerationItem>>({
      query: (data) => ({ url: '/moderation/queue', method: 'POST', body: data }),
      invalidatesTags: ['ModerationItem', 'ModerationStats'],
    }),

    takeModerationAction: builder.mutation<AdminApiResponse<{ item: ModerationItem; action: ModerationActionRecord }>, { id: string; action: ModerationActionType; reason?: string; newStatus?: ModerationStatus }>({
      query: ({ id, ...body }) => ({ url: `/moderation/queue/${id}/action`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'ModerationItem', id }, 'ModerationItem', 'ModerationStats'],
    }),

    bulkModerationAction: builder.mutation<AdminApiResponse<{ processed: number; action: string; newStatus: string }>, { ids: string[]; action: ModerationActionType; reason?: string }>({
      query: (body) => ({ url: '/moderation/queue/bulk-action', method: 'POST', body }),
      invalidatesTags: ['ModerationItem', 'ModerationStats'],
    }),

    // ─── Audit Logs ───────────────────────────────────────────────────────────

    getAuditLogs: builder.query<AdminApiResponse<AuditLogEntry[]>, AuditLogFilters>({
      query: (filters) => ({ url: '/audit-logs', params: filters }),
      providesTags: ['AuditLog'],
    }),

    getAuditStats: builder.query<AdminApiResponse<AuditStats>, { startDate?: string; endDate?: string }>({
      query: (params) => ({ url: '/audit-logs/stats', params }),
      providesTags: ['AuditLog'],
    }),

    archiveAuditLogs: builder.mutation<AdminApiResponse<{ archived: number }>, void>({
      query: () => ({ url: '/audit-logs/archive', method: 'POST' }),
      invalidatesTags: ['AuditLog'],
    }),

    verifyAuditLogIntegrity: builder.query<AdminApiResponse<{ id: string; integrityValid: boolean; timestamp: string }>, string>({
      query: (id) => `/audit-logs/${id}/verify`,
    }),

    exportAuditLogs: builder.query<Blob, AuditLogFilters>({
      query: (filters) => ({
        url: '/audit-logs/export',
        params: filters,
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),

    // ─── Role Management ──────────────────────────────────────────────────────

    getMyAdminPermissions: builder.query<AdminApiResponse<MyAdminPermissions>, void>({
      query: () => '/me/permissions',
    }),

    getRoleDefinitions: builder.query<AdminApiResponse<RoleDefinitionsPayload>, void>({
      query: () => '/roles',
      providesTags: ['RoleDefinition'],
    }),

    getSystemConfig: builder.query<
      AdminApiResponse<SystemConfigEntry[]>,
      { category?: string; includeInactive?: boolean } | void
    >({
      query: (params) => ({
        url: '/config',
        params: params
          ? {
              category: params.category,
              includeInactive: params.includeInactive ? 'true' : undefined,
            }
          : undefined,
      }),
      providesTags: ['SystemConfig'],
    }),

    upsertSystemConfig: builder.mutation<AdminApiResponse<SystemConfigEntry>, UpsertSystemConfigRequest>({
      query: (body) => ({ url: '/config', method: 'PUT', body }),
      invalidatesTags: ['SystemConfig'],
    }),

    deactivateSystemConfig: builder.mutation<AdminApiResponse<SystemConfigEntry>, string>({
      query: (key) => ({ url: `/config/${encodeURIComponent(key)}`, method: 'DELETE' }),
      invalidatesTags: ['SystemConfig'],
    }),

    getAdminIntegrations: builder.query<AdminApiResponse<AdminIntegration[]>, void>({
      query: () => '/integrations',
      providesTags: ['AdminIntegration'],
    }),

    updateAdminIntegration: builder.mutation<
      AdminApiResponse<AdminIntegration>,
      { id: string; status?: string; configuration?: Record<string, unknown> }
    >({
      query: ({ id, ...body }) => ({ url: `/integrations/${id}`, method: 'PUT', body }),
      invalidatesTags: ['AdminIntegration'],
    }),

    testAdminIntegration: builder.mutation<
      AdminApiResponse<{ integrationId: string; name: string; testedAt: string; result: string; message: string }>,
      string
    >({
      query: (id) => ({ url: `/integrations/${id}/test`, method: 'POST' }),
    }),

    getUserRoleHistory: builder.query<AdminApiResponse<AuditLogEntry[]>, string>({
      query: (userId) => `/roles/history/${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'AuditLog', id: userId }],
    }),

    assignUserRole: builder.mutation<AdminApiResponse<AdminUser>, RoleAssignment>({
      query: ({ userId, ...body }) => ({
        url: `/roles/assign/${userId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AdminUser', 'AuditLog'],
    }),
  }),
})

export const {
  useGetAdminDashboardQuery,
  useGetCatalogStatsQuery,
  useGetDetailedMetricsQuery,
  useGetFlaggedContentQuery,
  useModerateFlaggedContentMutation,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateAdminUserMutation,
  useUpdateUserMutation,
  useSuspendUserMutation,
  useActivateUserMutation,
  useGetSystemHealthQuery,
  useGetAnalyticsOverviewQuery,
  useGetUserAnalyticsQuery,
  useGetPerformanceAnalyticsQuery,
  useGetAdminFeatureUseSummaryQuery,
  useGetAdminCmsContentQuery,
  useCreateAdminCmsContentMutation,
  useUpdateAdminCmsContentMutation,
  useDeleteAdminCmsContentMutation,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useCreateCigarLineMutation,
  useUpdateCigarLineMutation,
  useDeleteCigarLineMutation,
  useGetAdminSubscriptionsQuery,
  useUpdateAdminSubscriptionMutation,
  useCancelAdminSubscriptionMutation,
  useRestoreAdminUserBackupMutation,
  useGetSupportTicketsQuery,
  useUpdateSupportTicketMutation,
  useRespondToTicketMutation,
  useGetModerationStatsQuery,
  useGetModerationQueueQuery,
  useGetModerationItemQuery,
  useCreateModerationItemMutation,
  useTakeModerationActionMutation,
  useBulkModerationActionMutation,
  useGetAuditLogsQuery,
  useGetAuditStatsQuery,
  useArchiveAuditLogsMutation,
  useVerifyAuditLogIntegrityQuery,
  useLazyVerifyAuditLogIntegrityQuery,
  useLazyExportAuditLogsQuery,
  useGetMyAdminPermissionsQuery,
  useGetRoleDefinitionsQuery,
  useGetUserRoleHistoryQuery,
  useAssignUserRoleMutation,
  useGetSystemConfigQuery,
  useUpsertSystemConfigMutation,
  useDeactivateSystemConfigMutation,
  useGetAdminIntegrationsQuery,
  useUpdateAdminIntegrationMutation,
  useTestAdminIntegrationMutation,
} = adminApi
